// Boton "Reintentar cobro ahora" + confirmacion + resultado.
//
// Lo usan la lista de cobros pendientes y la ficha del cliente. El cobro es
// real y sale en el acto (el backend invoca al cobrador y espera la
// respuesta), asi que el dialogo dice exactamente que puede pasar, y el
// resultado queda a la vista hasta que el operador lo cierre.

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, CircleAlert, CreditCard, Info, X } from 'lucide-react';
import {
  retrySubscriptionCharge,
  AdminApiError,
  type ChargeRetryResult,
} from '@/lib/api';
import { money, shortDate } from '@/lib/format';
import { declineLabel, technicalLabel } from '@/lib/charge-state';
import ConfirmDialog from '@/components/ConfirmDialog';
import { fieldCls } from '@/lib/form-styles';

export interface ChargeRetryTarget {
  subscriptionId: string;
  status: string;
  customer?: string | null;
  planName?: string | null;
  amount?: string | null;
  currency?: string | null;
}

export type RetryNoticeKind = 'ok' | 'warn' | 'error' | 'info';

/** Texto del resultado. Exportado: la lista lo usa para el reintento en lote. */
export function describeRetry(r: ChargeRetryResult): { kind: RetryNoticeKind; text: string } {
  switch (r.kind) {
    case 'succeeded':
      return {
        kind: 'ok',
        text: `Cobrado${r.amount ? ` ${money(r.amount)}` : ''}.${
          r.period_end ? ` Queda al día hasta el ${shortDate(r.period_end)}.` : ''
        }${r.message?.includes('reactivada') ? ' La suscripción se reactivó.' : ''}`,
      };
    case 'declined':
      return {
        kind: 'warn',
        text: `El banco lo rechazó: ${declineLabel(r.failure_code) ?? r.message ?? 'sin motivo'}. La suscripción queda como estaba.`,
      };
    case 'error':
      return {
        kind: 'error',
        text: `No se pudo cobrar: ${technicalLabel(r.error_code) ?? 'error del procesador'}.${
          r.message ? ` (${r.message})` : ''
        } No es un rechazo de la tarjeta.`,
      };
    case 'duplicate':
      return { kind: 'info', text: r.message ?? 'Ese cobro ya estaba hecho: no se volvió a cobrar.' };
    case 'skipped_lock':
      return { kind: 'info', text: 'Hay otro cobro de esta suscripción en curso. Probá en un minuto.' };
    case 'refused':
      return { kind: 'info', text: r.message ?? 'No hay nada para cobrar.' };
  }
}

export function RetryNotice({
  kind,
  text,
  onClose,
}: {
  kind: RetryNoticeKind;
  text: string;
  onClose?: () => void;
}): JSX.Element {
  const cls = {
    ok: 'border-status-success/40 bg-status-success/10 text-status-success',
    warn: 'border-status-warning/40 bg-status-warning/10 text-status-warning',
    error: 'border-status-error/40 bg-status-error/10 text-status-error',
    info: 'border-surface-border dark:border-surface-dark-border bg-ink-muted/10 text-ink-muted',
  }[kind];
  const Icon = kind === 'ok' ? Check : kind === 'info' ? Info : CircleAlert;
  return (
    <div role="status" className={`rounded-lg border px-3 py-2 text-sm flex items-start gap-2 ${cls}`}>
      <Icon size={16} className="shrink-0 mt-0.5" aria-hidden="true" />
      <span className="flex-1">{text}</span>
      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          className="shrink-0 rounded p-0.5 hover:bg-ink-muted/15 focus-visible:ring-2 focus-visible:ring-brand-light"
        >
          <X size={14} aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

/** El texto de la confirmacion. Tambien lo usa el reintento en lote. */
export function RetryExplanation({ expired }: { expired: boolean }): JSX.Element {
  return (
    <ul className="list-disc pl-5 space-y-1 text-xs">
      <li>Se intenta cobrar ahora mismo a la tarjeta guardada. Es un cobro real.</li>
      <li>
        Si el banco lo rechaza, la suscripción queda{' '}
        <strong className="text-ink dark:text-ink-inverted">exactamente como estaba</strong>: no se
        gasta un reintento ni se da de baja.
      </li>
      {expired ? (
        <li>
          Está dada de baja: si el cobro pasa, se reactiva con un período nuevo que empieza hoy. No
          se cobran los meses que estuvo cortada.
        </li>
      ) : null}
    </ul>
  );
}

export default function ChargeRetryButton({
  target,
  disabled,
  disabledReason,
  size = 'md',
  onDone,
}: {
  target: ChargeRetryTarget;
  disabled?: boolean;
  disabledReason?: string;
  size?: 'sm' | 'md';
  onDone?: (r: ChargeRetryResult) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [notice, setNotice] = useState<{ kind: RetryNoticeKind; text: string } | null>(null);

  const mut = useMutation({
    mutationFn: () => retrySubscriptionCharge(target.subscriptionId, reason.trim() || undefined),
    onSuccess: (r) => {
      setOpen(false);
      setReason('');
      setNotice(describeRetry(r));
      qc.invalidateQueries({ queryKey: ['admin-user-detail'] });
      qc.invalidateQueries({ queryKey: ['admin-pending-charges'] });
      onDone?.(r);
    },
  });

  const amount = target.amount ? money(target.amount, target.currency ?? 'ARS') : null;
  const btnSize = size === 'sm' ? 'min-h-[2rem] px-2.5 text-xs' : 'min-h-[2.25rem] px-3 text-sm';

  return (
    <div className="space-y-2">
      <button
        type="button"
        data-help="charges.retry"
        onClick={() => {
          setNotice(null);
          setOpen(true);
        }}
        disabled={disabled}
        title={disabled ? disabledReason : undefined}
        className={`${btnSize} inline-flex items-center gap-2 rounded-lg font-medium bg-brand text-white hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-brand-light`}
      >
        <CreditCard size={size === 'sm' ? 14 : 16} aria-hidden="true" />
        Reintentar cobro ahora
      </button>
      {notice ? <RetryNotice {...notice} onClose={() => setNotice(null)} /> : null}

      <ConfirmDialog
        open={open}
        title="Reintentar el cobro ahora"
        description={
          <div className="space-y-3">
            <p>
              {amount ? (
                <>
                  Se van a cobrar <strong className="text-ink dark:text-ink-inverted">{amount}</strong>
                </>
              ) : (
                'Se va a cobrar el plan'
              )}
              {target.planName ? ` (${target.planName})` : ''}
              {target.customer ? ` a ${target.customer}` : ''}.
            </p>
            <RetryExplanation expired={target.status === 'expired'} />
            <label className="block text-xs">
              <span className="font-medium">Motivo (queda en la auditoría, opcional)</span>
              <input
                type="text"
                value={reason}
                maxLength={500}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ej.: el cliente avisó que cargó fondos"
                className={fieldCls({ extra: 'mt-1' })}
              />
            </label>
            {mut.isError ? (
              <p className="text-status-error text-xs">
                Error: {mut.error instanceof AdminApiError ? mut.error.message : 'desconocido'}
              </p>
            ) : null}
          </div>
        }
        confirmLabel={mut.isPending ? 'Cobrando…' : 'Cobrar ahora'}
        loading={mut.isPending}
        onConfirm={() => mut.mutate()}
        onCancel={() => {
          setOpen(false);
          mut.reset();
        }}
      />
    </div>
  );
}
